/**
 * PromptTemplate Component
 * Handles custom node prompt templates with variable replacement and AI execution
 */

import React, { useState, useCallback } from 'react';
import SendIcon from '@mui/icons-material/Send';
import { createPromptFromTemplate, extractVariables } from '../../utils/promptTemplateUtils';
import OpenAIService from '../../services/OpenAIService';
import GoogleAIService from '../../services/GoogleAIService';
import VeoVideoService from '../../services/VeoVideoService';
import { supabaseFlowStorageService as flowStorageService } from '../../services/SupabaseFlowStorageService';
import logger from '../../utils/logger';
import type { ConversationContext } from '../../types/api';

export interface PromptTemplateProps {
  // Configuration
  template: string;                       // Template with {{variables}}
  variables: Record<string, string>;      // Variable values from inputs
  aiModel: string;                        // Which AI model to use
  
  // Optional fields
  hasAdditionalInstructions?: boolean;
  additionalInstructionsLabel?: string;
  additionalInstructionsValue?: string;
  onAdditionalInstructionsChange?: (value: string) => void;
  
  // Model parameters
  parameters?: Record<string, any>;
  
  // Callbacks
  onSubmit?: () => void;
  onExecute?: (result: any) => void;
  onProcessingChange?: (isProcessing: boolean) => void;
  
  // State
  disabled?: boolean;
  variant?: 'positive' | 'negative' | 'neutral' | 'purple';
  
  // Node context (for threading, file contexts, etc.)
  nodeId: string;
  threadId?: string;
  context?: ConversationContext;
}

export const PromptTemplate: React.FC<PromptTemplateProps> = ({
  template,
  variables,
  aiModel,
  hasAdditionalInstructions = false,
  additionalInstructionsLabel = 'Additional Instructions',
  additionalInstructionsValue = '',
  onAdditionalInstructionsChange,
  parameters = {},
  onSubmit,
  onExecute,
  onProcessingChange,
  disabled = false,
  // variant prop received but not used currently
  nodeId,
  context
}) => {
  const [localAdditionalInstructions, setLocalAdditionalInstructions] = useState(additionalInstructionsValue);
  const [isProcessing, setIsProcessing] = useState(false);
  
  // Debug: Log render
  React.useEffect(() => {
    logger.debug('[PromptTemplate] Rendered:', {
      nodeId,
      template: template.substring(0, 50),
      variableCount: Object.keys(variables).length,
      aiModel,
      hasAdditionalInstructions
    });
  }, [nodeId, template, variables, aiModel, hasAdditionalInstructions]);
  
  // Use controlled value if provided, otherwise use local state
  const additionalInstructions = onAdditionalInstructionsChange 
    ? additionalInstructionsValue 
    : localAdditionalInstructions;
  
  const handleAdditionalInstructionsChange = (value: string) => {
    if (onAdditionalInstructionsChange) {
      onAdditionalInstructionsChange(value);
    } else {
      setLocalAdditionalInstructions(value);
    }
  };
  
  // Extract and display variables from template
  const detectedVars = extractVariables(template);
  
  // Execute the prompt
  const handleExecute = useCallback(async () => {
    // Update processing state
    setIsProcessing(true);
    if (onProcessingChange) {
      onProcessingChange(true);
    }
    
    try {
      logger.info('[PromptTemplate] Executing:', {
        nodeId,
        aiModel,
        template: template.substring(0, 100) + '...',
        variableCount: Object.keys(variables).length
      });
      
      // 1. Replace {{variables}} in template
      const result = createPromptFromTemplate(template, variables, {
        allowMissing: true,
        sanitize: true
      });
      
      if (!result.success) {
        throw new Error(result.error || 'Template processing failed');
      }
      
      let finalPrompt = result.prompt!;
      
      // 2. Append additional instructions
      if (hasAdditionalInstructions && additionalInstructions.trim()) {
        finalPrompt += `\n\n${additionalInstructionsLabel}: ${additionalInstructions}`;
      }
      
      logger.debug('[PromptTemplate] Final prompt:', finalPrompt);
      
      // 3. Build enhanced context with image/video inputs from variables
      // If any variable contains a URL (starts with http/https/data:), add it to context messages
      const imageVariables = Object.entries(variables)
        .filter(([_, value]) => {
          const val = String(value);
          return (val.startsWith('http://') || 
                  val.startsWith('https://') || 
                  val.startsWith('data:image/')) &&
                 (val.includes('image') || val.match(/\.(jpg|jpeg|png|gif|webp)$/i));
        });
      
      let enhancedContext = context;
      
      if (imageVariables.length > 0) {
        logger.debug('[PromptTemplate] Found image variables:', imageVariables.length);
        
        // Build context messages with image parts
        const existingMessages = context?.messages || [];
        const imageMessages: any[] = [];
        
        for (const [varName, imageUrl] of imageVariables) {
          const url = String(imageUrl);
          imageMessages.push({
            role: 'user' as const,
            content: [
              {
                type: 'image' as const,
                imageUrl: url,
                mimeType: url.startsWith('data:') 
                  ? url.split(';')[0].split(':')[1] 
                  : 'image/png'
              }
            ]
          });
          logger.debug('[PromptTemplate] Added image to context:', varName);
        }
        
        enhancedContext = {
          ...context,
          messages: [
            ...existingMessages,
            ...imageMessages
          ]
        };
      }
      
      // 3. Call appropriate AI service based on model
      let response;
      let outputType: 'text' | 'image' | 'video' = 'text';
      
      if (aiModel === 'gpt-4o-mini') {
        // OpenAI text generation with streaming
        let streamedContent = '';
        response = await OpenAIService.generateResponseStream(
          finalPrompt,
          null,
          enhancedContext,
          (delta: string) => {
            streamedContent += delta;
            // Stream updates handled by parent via onExecute
            if (onExecute) {
              onExecute({
                content: streamedContent,
                type: 'text',
                streaming: true,
                nodeId
              });
            }
          }
        );
        outputType = 'text';
        
      } else if (aiModel === 'gemini-2.5-flash') {
        // Google text generation
        response = await GoogleAIService.generateText(finalPrompt, enhancedContext);
        outputType = 'text';
        
      } else if (aiModel === 'nano-banana') {
        // Google image generation
        if (!GoogleAIService.isConfigured()) {
          throw new Error('Google API key not configured. Please check your .env file.');
        }
        response = await GoogleAIService.generateImage(
          finalPrompt,
          enhancedContext,
          parameters.aspectRatio || '9:16'
        );
        outputType = 'image';
        
      } else if (aiModel === 'veo-3.1-fast' || aiModel === 'veo-3.0-fast') {
        // Google video generation (VEO)
        if (!VeoVideoService.isConfigured()) {
          throw new Error('Google API key not configured. Please check your .env file.');
        }
        
        const videoModel = parameters.model || 
          (aiModel === 'veo-3.1-fast' 
            ? 'veo-3.1-fast-generate-preview' 
            : 'veo-3.0-fast-generate-001');
        
        const currentFlowId = flowStorageService.getCurrentFlowId();
        response = await VeoVideoService.generateVideo(
          finalPrompt,
          enhancedContext,
          parameters.aspectRatio || '9:16',
          videoModel,
          parameters.duration || '8',
          nodeId,  // nodeId for job persistence
          currentFlowId || undefined  // flowId for job persistence
        );
        outputType = 'video';

      } else {
        throw new Error(`Unsupported AI model: ${aiModel}`);
      }
      
      logger.info('[PromptTemplate] Execution complete:', {
        nodeId,
        outputType,
        hasContent: !!response.content
      });
      
      // 4. Send result to parent
      if (onExecute) {
        const result = {
          nodeId,
          content: response.content,
          type: outputType,
          context: response.context,
          streaming: false,
          ...(outputType === 'video' && (response as any).videoUrl && { 
            videoUrl: (response as any).videoUrl 
          })
        };
        
        onExecute(result);
      }
      
    } catch (error) {
      logger.error('[PromptTemplate] Execution failed:', error);
      throw error;
    } finally {
      setIsProcessing(false);
      if (onProcessingChange) {
        onProcessingChange(false);
      }
    }
  }, [
    template,
    variables,
    aiModel,
    hasAdditionalInstructions,
    additionalInstructions,
    additionalInstructionsLabel,
    parameters,
    nodeId,
    context,
    onExecute,
    onProcessingChange
  ]);
  
  // Handle Ctrl+Enter to execute
  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.ctrlKey && e.key === 'Enter' && !isProcessing && !disabled) {
      handleExecute();
      if (onSubmit) onSubmit();
    }
  }, [handleExecute, isProcessing, disabled, onSubmit]);
  
  return (
    <div className="prompt-template-component" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
      {/* Show detected variables */}
      {detectedVars.length > 0 && (
        <div 
          className="template-variables" 
          style={{ 
            fontSize: '12px', 
            color: 'var(--color-text-secondary)',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px',
            alignItems: 'center'
          }}
        >
          <span>Variables:</span>
          {detectedVars.map(v => {
            const hasValue = variables[v] && variables[v].trim().length > 0;
            return (
              <span 
                key={v} 
                style={{ 
                  padding: '2px 6px',
                  borderRadius: '3px',
                  backgroundColor: hasValue 
                    ? 'rgba(34, 197, 94, 0.2)' 
                    : 'rgba(239, 68, 68, 0.2)',
                  color: hasValue 
                    ? 'var(--color-text-primary)' 
                    : 'var(--color-text-secondary)',
                  fontSize: '11px',
                  fontFamily: 'monospace'
                }}
              >
                {`{{${v}}}`}
                <span style={{ marginLeft: '4px' }}>
                  {hasValue ? '✓' : '⚠️'}
                </span>
              </span>
            );
          })}
        </div>
      )}
      
      {/* Additional instructions field */}
      {hasAdditionalInstructions && (
        <div style={{ position: 'relative', display: 'flex', alignItems: 'flex-end', flex: 1, minHeight: 0 }}>
          <textarea
            value={additionalInstructions}
            onChange={(e) => handleAdditionalInstructionsChange(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={`${additionalInstructionsLabel}...`}
            className="nodrag textarea-control"
            disabled={disabled || isProcessing}
            style={{
              flex: 1,
              resize: 'none',
              minHeight: '80px'
            }}
          />
          <button
            type="button"
            aria-label="Execute"
            onClick={() => {
              handleExecute();
            }}
            className="send-button"
            disabled={disabled || isProcessing}
          >
            <SendIcon fontSize="medium" />
          </button>
        </div>
      )}
      
      {/* Execute button (if no additional instructions field) */}
      {!hasAdditionalInstructions && (
        <button
          onClick={() => {
            handleExecute();
          }}
          disabled={disabled || isProcessing}
          style={{
            padding: '8px 16px',
            background: 'var(--color-accent-primary)',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: disabled || isProcessing ? 'not-allowed' : 'pointer',
            fontSize: '14px',
            fontWeight: 500,
            opacity: disabled || isProcessing ? 0.5 : 1,
            transition: 'opacity 0.2s'
          }}
        >
          {isProcessing ? 'Executing...' : `Execute with ${aiModel}`}
        </button>
      )}
    </div>
  );
};
